// My roster this week, beside the news on a desktop (B-395): every player of
// mine with a game status or on a bye, the out first, each with what the
// report says. What the news says about my players, as a list I can read at
// a glance rather than a feed I scroll. Facts only: who, and what is listed.

import { valueOf, type PlayerRowView } from '@fsp/domain';
import { useLeagueBase, usePlayers, useReaderZone } from '../../data/hooks';
import { PlayerMarks, PositionPill } from '../../kit';
import { Button, Caption, ListRow, RowName, RowSub, Text } from '../../kit/ui';
import { PlayerName } from '../shared/Names';
import { useTools } from '../../data/tools';
import { injuryWords } from '../shared/injuryWords';

/** Out, then likely out, then a lesser listing, then a bye. */
function weight(r: PlayerRowView): number {
  const severity = r.player.injury?.severity ?? 'none';
  if (severity === 'out') return 0;
  if (severity === 'likely-out') return 1;
  if (severity === 'warn') return 2;
  return 3;
}

/** Listed on the report with something to say, or on a bye. */
function flagged(r: PlayerRowView): boolean {
  return r.player.onBye || (r.player.injury !== null && r.player.injury.severity !== 'none');
}

export function RosterReport() {
  const base = useLeagueBase();
  const tools = useTools();
  const zone = useReaderZone();
  const read = usePlayers({
    availability: 'mine',
    positions: [],
    q: '',
    // By points a game in a league that keeps no projections (§15c).
    sort: tools.projections ? 'projection' : 'average',
    reverse: false,
    cursor: null,
    limit: 50,
  });
  const rows = valueOf(read.snapshot)?.items ?? null;
  const listed = (rows ?? []).filter(flagged).sort((a, b) => weight(a) - weight(b));
  return (
    <>
      <div className="fsp-region-head">
        <Caption>Your roster this week</Caption>
        <Button size="xs" variant="quiet" to={base}>
          My Team
        </Button>
      </div>
      {rows === null ? (
        <div className="fsp-news-side-note">
          <Text tone="secondary" size="xs">
            Reading your roster…
          </Text>
        </div>
      ) : listed.length === 0 ? (
        <div className="fsp-news-side-note">
          <Text tone="secondary" size="xs">
            Nobody on your roster is on the injury report or on a bye this week.
          </Text>
        </div>
      ) : (
        listed.map((r) => (
          <ListRow
            key={r.player.id}
            lead={<PositionPill pos={r.player.positions[0] ?? '—'} size="sm" />}
            tall
          >
            <RowName marks={<PlayerMarks player={r.player} />}>
              <PlayerName player={r.player} />
            </RowName>
            <RowSub wrap>
              {r.player.injury !== null && r.player.injury.severity !== 'none'
                ? injuryWords(r.player.injury, zone, ': ')
                : 'On a bye this week'}
            </RowSub>
          </ListRow>
        ))
      )}
    </>
  );
}
